import React, { useEffect, useMemo, useState } from 'react';

import { AppBar, Box, Button, Container, Grid, IconButton, makeStyles, Menu, MenuItem, Toolbar, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { AccountCircle } from '@material-ui/icons';
import { Link, useLocation } from 'react-router-dom';

import packageJson from '../../../package.json';
import IconChatFillWhite from '../../assets/images/ic-chat-fill-white.svg';
import logoSvg from '../../assets/images/logo-white.svg';
import { ROLES_LABELS } from '../../constants/general';
import Notifications from '../../containers/notifications/Notifications';
import { useAppContext } from '../../contexts/AppContext';
import { WorkspaceProductType } from '../../models/Workspace';

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: '#172B4D',
    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.1)',
    color: '#FFF',
  },
  chatImage: {
    display: 'inline-block',
    height: 'auto',
    width: '22px',
  },
  chatText: {
    color: '#FFFFFF',
    display: 'none',
    fontSize: '10px',
    fontWeight: '500',
    lineHeight: '12px',
    marginBottom: '0px !important',
    marginRight: '6px',
  },
  flexContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  iconsContainer: {
    justifyContent: 'flex-end',
  },
  logo: {
    marginRight: theme.spacing(3),
    maxHeight: '35px',
    maxWidth: '144px',
  },
  menuContainer: {
    '& .MuiPaper-root': {
      '& ul .MuiGrid-root': {
        '&:focus': {
          outline: 'none',
        },
      },
      border: '1px solid #E7E7E7',
      boxShadow: '2px 6px 10px rgba(0, 0, 0, 0.1)',
    },
  },
  menuItem: {
    color: theme.palette.primary.main,
    fontSize: '14px',
  },
  navContainer: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    widht: '100%',
  },
  navLink: {
    color: '#172B4D',
    fontSize: '14px',
    textDecoration: 'none',
  },
  navLinkBorder: {
    borderLeft: '1px solid #172B4D4C',
    paddingLeft: '1rem',
  },
  navLinkContainer: (props) => ({
    '&:hover a': {
      color: props.isTechQA ? '#2AAAE2 !important' : `${theme.palette.primary.main} !important`,
    },
    display: 'inline-block',
    lineHeight: '1rem',
    paddingRight: '1rem',
  }),
  navLinkSelected: (props) => ({
    color: props.isTechQA ? '#2AAAE2' : theme.palette.primary.main,
    fontWeight: 'bold',
  }),
  responsive: {
    [theme.breakpoints.down('sm')]: {
      display: 'none !important',
    },
  },
  root: {
    flexGrow: 1,
  },
  submenuContainer: {
    alignItems: 'center',
    background: '#F5F5F5',
    display: 'flex',
    height: '36px',
    justifyContent: 'flex-start',
    zIndex: 2,

    [theme.breakpoints.down('sm')]: {
      display: 'none !important',
    },
  },
  submenuFlexItems: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-start',
    marginRight: '8px',
  },
  submenuWrapper: {
    alignItems: 'center',
    color: '#172B4D',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
  },
  underline: {
    textDecoration: 'underline !important',
  },
  userInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    marginLeft: 16,
    marginRight: 4,
    textAlign: 'left',
  },
  userName: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '100%',
  },
  userRole: (props) => ({
    color: props.isTechQA ? '#2AAAE2' : theme.palette.primary.main,
    fontSize: 10,
    fontWeight: 400,
    lineHeight: '100%',
  }),
  userRoleOld: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 500,
    marginRight: 16,
    padding: '8px 16px',
    textAlign: 'center',
  },
  version: {
    alignItems: 'center',
    display: 'flex',
    paddingLeft: 4,
    textAlign: 'center',
  },
  [theme.breakpoints.up('sm')]: {
    chatText: {
      display: 'inline-block',
    },
  },
}));

export default ({ menu, onLogoClick, onLogout, onNavigation, logoUrl }) => {
  const { currentUser, currentWorkspace } = useAppContext();
  const isTechQA = currentWorkspace?.productType === WorkspaceProductType.TechQA.value;
  const classes = useStyles({ isTechQA });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuObject, setMenuObject] = useState(menu);
  const [logo, setLogo] = useState(null);
  const location = useLocation();
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (logoUrl) setLogo(logoUrl);
  }, [logoUrl]);

  useEffect(() => {
    if (menu) setMenuObject(menu);
  }, [menu]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNavigation = (route) => {
    onNavigation(route);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const managedMenuItems = useMemo(() => {
    const menuItems = menuObject.routes.map((item, index) => {
      const classSetup = [classes.navLink];
      if (location.pathname.includes(item.path)) classSetup.push(classes.navLinkSelected);

      const pathClassName = `appbar-navlink${(item.path || '').replaceAll('/', '--')}`;
      classSetup.push(pathClassName);

      const parentClassSetup = [classes.navLinkContainer, classes.responsive];
      if (index > 0) parentClassSetup.push(classes.navLinkBorder);

      return (
        <Typography variant="body1" className={parentClassSetup.join(' ')} key={item.path}>
          <Link className={classSetup.join(' ')} to={item.path}>
            {item.name}
          </Link>
        </Typography>
      );
    });

    if (menuObject.button && menuObject.button.shouldAppear() && !menuObject.button.shouldListedAsButton) {
      if (menuObject.button.route.includes('/personalized-video') || menuObject.button.route.includes('/package-plan')) {
        if (location.pathname.includes('/personalized-video')) return menuItems;
      }

      const parentClassSetup = [classes.navLinkContainer, classes.responsive];
      if (menuItems.length) parentClassSetup.push(classes.navLinkBorder);

      const classSetup = [classes.navLink];
      const path = menuObject.button.route.split('?')?.[0] || '';
      const pathClassName = `appbar-navlink${(path || '').replaceAll('/', '--')}`;

      if (location.pathname.includes(path)) classSetup.push(classes.navLinkSelected);
      classSetup.push(pathClassName);

      menuItems.push(
        <Typography variant="body1" className={parentClassSetup.join(' ')} key={`appbar-cta--${menuObject.button.route}`}>
          <Link className={classSetup.join(' ')} to={menuObject.button.route}>
            {menuObject.button.text}
          </Link>
        </Typography>
      );
    }

    return menuItems;
  }, [menuObject, classes, location]);

  const managedRightCtaButton = useMemo(() => {
    if (!menuObject.button?.shouldAppear() || !menuObject.button.shouldListedAsButton) return null;

    const path = menuObject.button.route.split('?')?.[0] || '';
    const pathClassName = `appbar-navlink${(path || '').replaceAll('/', '--')}`;

    return (
      <Link className={pathClassName} style={{ textDecoration: 'none' }} to={menuObject.button.route}>
        <Button style={{ height: '32px' }} variant="contained" color="primary" disableElevation>
          {menuObject?.button?.text || 'Text Not Found'}
        </Button>
      </Link>
    );
  }, [menuObject]);

  const userMenuItems = menuObject.userMenu.map((item) => {
    if (item.shouldAppear && !item.shouldAppear()) {
      return null;
    }

    // Check if this item is a route that's shown in the main horizontal menu
    const isRouteItem = menuObject.routes?.some((route) => route.path === item.path);

    // Only show route items in the dropdown when the horizontal menu is hidden (small screens)
    // Always show non-route items (like "My Account", "Logout")
    if (isRouteItem && !isSmallScreen) {
      return null;
    }

    const classNameSetup = `appbar-menuitem${(item.path || '').replaceAll('/', '--')}`;

    return (
      <MenuItem className={`${classes.menuItem} ${classNameSetup}`} onClick={() => handleNavigation(item.path)} key={item.path}>
        {item.name}
      </MenuItem>
    );
  });

  const showVersion = process.env.REACT_APP_SHOW_VERSION ? !!JSON.parse(process.env.REACT_APP_SHOW_VERSION) : false;
  const versionDiv = showVersion ? <div className={[classes.version, classes.responsive].join(' ')}>Version: {packageJson.version}</div> : null;

  return (
    <AppBar position="static" className={classes.appBar}>
      <Toolbar>
        <Grid container className={classes.navContainer} justifyContent="space-between" style={{ width: '100%' }}>
          <Grid item className={classes.flexContainer}>
            <Link onClick={onLogoClick} to={menuObject?.logoPath || '/'}>
              <img className={classes.logo} style={{ width: logo ? '' : '100%' }} src={logo || logoSvg} alt="logo" />
            </Link>
            {versionDiv}
          </Grid>
          <Grid item className={`${classes.flexContainer} ${classes.iconsContainer}`}>
            {currentWorkspace?.productType !== WorkspaceProductType.TechQA.value && (
              <a href="https://mediphany.com/faq/" color="inherit" target="_blank" rel="noreferrer" style={{ alignItems: 'center', display: 'flex' }}>
                <span className={classes.chatText}>FAQ</span>
                <img className={classes.chatImage} src={IconChatFillWhite} alt="chat icon" />
              </a>
            )}

            <Notifications />

            {currentUser?.role && currentUser.role !== 'patient' && (
              <Box className={classes.userInfoContainer}>
                <Typography className={classes.userName}>{currentUser.name}</Typography>
                <Typography className={classes.userRole}>{ROLES_LABELS[currentUser.role]}</Typography>
              </Box>
            )}

            <IconButton
              id="appbarMenuIconAccount"
              edge="end"
              aria-label="account of current user"
              color="inherit"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}>
              <AccountCircle />
            </IconButton>

            <Menu
              id="menu-appbar"
              elevation={0}
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              className={classes.menuContainer}
              anchorOrigin={{
                horizontal: 'center',
                vertical: 'bottom',
              }}
              keepMounted
              transformOrigin={{
                horizontal: 'center',
                vertical: 'top',
              }}
              open={open}
              onClose={handleClose}>
              {userMenuItems}
              <MenuItem className={classes.menuItem} style={{ marginTop: '1rem' }} onClick={onLogout}>
                Logout
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>
      </Toolbar>

      <div className={classes.submenuContainer}>
        <Container maxWidth={false}>
          <div className={classes.submenuWrapper}>
            <div className={classes.submenuFlexItems}>{managedMenuItems}</div>

            {managedRightCtaButton}
          </div>
        </Container>
      </div>
    </AppBar>
  );
};
